<template>
  <div class="rqa_thread_view">
    <div class="rqa_thread_top">
      <button ref="back" type="button" class="rqa_back" @click="back">
        <span aria-hidden="true">→</span> חזרה לפניות
      </button>
    </div>

    <header class="rqa_thread_header">
      <div class="rqa_thread_title_row">
        <h3 class="rqa_thread_title">{{ title }}</h3>
        <span class="rqa_muted rqa_small rqa_nowrap">#{{ rootId }}</span>
      </div>
      <p v-if="originalOwnerName" class="rqa_muted rqa_small rqa_transferred_from">הועבר מ: {{ originalOwnerName }}</p>
      <div class="rqa_thread_meta">
        <recruiter-qa-status-pill v-if="headerStatus" :status="headerStatus" />
        <span v-if="isClosed" class="rqa_closed_label">סגורה</span>
        <button
          v-if="ticket && !foreignOwner"
          type="button"
          class="button is-small rqa_lifecycle"
          :class="{ 'is-loading': lifecycleBusy }"
          @click="toggleLifecycle">{{ isClosed ? 'פתיחה מחדש' : 'סגירת פנייה' }}</button>
      </div>
      <div v-if="ticket && !isClosed && confirmingClose" class="rqa_close_confirm" role="group" aria-label="אישור סגירת הפנייה">
        <p class="rqa_muted rqa_small rqa_hint">
          לסגור את הפנייה? תשובה חדשה תפתח את הפנייה מחדש<template v-if="jiraIds.length">. סגירת הפנייה אינה מבטלת את המשימה</template>
        </p>
        <div class="rqa_close_confirm_actions">
          <button ref="confirmClose" type="button" class="button is-small is-primary" :class="{ 'is-loading': lifecycleBusy }" @click="confirmClose">סגירה</button>
          <button type="button" class="button is-small" :disabled="lifecycleBusy" @click="cancelClose">ביטול</button>
        </div>
      </div>
      <p v-if="lifecycleError" class="rqa_error" role="alert">{{ lifecycleError }}</p>
      <div v-for="jid in jiraIds" :key="'jira_' + jid" class="rqa_jira">
        <span class="rqa_small">משימה מקושרת: {{ jiraShort(jid) }}</span>
        <button type="button" class="button is-small" :disabled="isClosed || sending" @click="checkJira(jid)">בדקי סטטוס</button>
      </div>
    </header>

    <section ref="turns" class="rqa_turns">
      <p v-if="thread && thread.loading && !turns.length" class="rqa_muted rqa_center" role="status">טוענת את הפנייה…</p>
      <div v-if="thread && thread.error" class="rqa_center">
        <p class="rqa_error" role="alert">{{ thread.error }}</p>
        <button type="button" class="button is-small" @click="reload">טעינה מחדש</button>
      </div>
      <p v-if="thread && thread.truncated" class="rqa_muted rqa_small rqa_truncated">מוצגות 200 ההודעות האחרונות בלבד; הודעות ישנות יותר לא מוצגות.</p>

      <div v-for="turn in turns" :key="turn.id" class="rqa_turn" :class="{ rqa_turn_reanswer: reAnswer(turn), rqa_turn_system: systemNote(turn) }">
        <!-- A re-answer is answer-only: the recruiter's question is not repeated. -->
        <p v-if="reAnswer(turn)" class="rqa_reanswer_label rqa_small">{{ reAnswerLabel }}</p>
        <template v-else-if="systemNote(turn)"></template>
        <template v-else>
          <div class="rqa_turn_head">
            <recruiter-qa-status-pill v-if="statusOf(turn)" :status="statusOf(turn)" />
            <span v-if="contextLine(turn)" class="rqa_muted rqa_small rqa_ctx">{{ contextLine(turn) }}</span>
          </div>
          <p class="rqa_question">{{ turn.question }}</p>
          <recruiter-qa-attachment v-if="turn.attachment" :question-id="turn.id" />
        </template>
        <div v-if="answerText(turn)" class="rqa_answer">
          <recruiter-qa-entity-text text-class="rqa_answer_text" :segments="answerSegments(turn)" />
          <div v-if="turn.answer && turn.answer_long">
            <a href="#" class="rqa_link" @click.prevent="toggle('long', turn.id)">{{ isOpen('long', turn.id) ? 'פחות' : 'פירוט' }}</a>
            <recruiter-qa-entity-text v-if="isOpen('long', turn.id)" text-class="rqa_answer_long" :segments="segmentsOf(turn, 'answer_long')" />
          </div>
          <recruiter-qa-entity-chips :entities="relatedOf(turn)" />
          <div v-if="turn.sources && turn.sources.length">
            <a href="#" class="rqa_link rqa_small" @click.prevent="toggle('src', turn.id)">מקורות ({{ turn.sources.length }})</a>
            <ul v-if="isOpen('src', turn.id)" class="rqa_sources">
              <li v-for="(src, i) in turn.sources" :key="i" class="rqa_small">{{ sourceText(src) }}</li>
            </ul>
          </div>
          <div v-if="feedbackAllowed(turn)" class="rqa_feedback">
            <template v-if="feedback[turn.id]">
              <span class="rqa_muted rqa_small">תודה על המשוב</span>
            </template>
            <template v-else-if="reasonFor === turn.id">
              <input v-model="reasonDraft" class="rqa_reason" maxlength="500" placeholder="מה היה חסר? (לא חובה)" @keydown.enter.prevent="submitFeedback(turn.id, false)" />
              <button type="button" class="button is-small" @click="submitFeedback(turn.id, false)">שליחת משוב</button>
              <button type="button" class="button is-small is-white" @click="reasonFor = null">ביטול</button>
            </template>
            <template v-else>
              <button type="button" class="button is-small" @click="submitFeedback(turn.id, true)">עזר</button>
              <button type="button" class="button is-small" @click="askReason(turn.id)">לא עזר</button>
            </template>
          </div>
        </div>
        <p v-else-if="isPendingStatus(turn.status)" class="rqa_muted rqa_small">התשובה תופיע כאן כשתהיה מוכנה.</p>
        <p v-if="cancelled(turn)" class="rqa_muted rqa_small rqa_cancelled">{{ cancelledText }}</p>
        <div v-else-if="retryAllowed(turn)" class="rqa_failed">
          <span class="rqa_muted rqa_small">לא הצלחנו לענות על השאלה הזו.</span>
          <button type="button" class="button is-small" :class="{ 'is-loading': retrying === turn.id }" :disabled="isClosed" @click="retry(turn.id)">נסי שוב</button>
        </div>
        <p v-if="turnErrors[turn.id]" class="rqa_error" role="alert">{{ turnErrors[turn.id] }}</p>
        <p v-if="refreshAfterId === turn.id" class="rqa_muted rqa_small rqa_refresh_hint" role="status">{{ refreshText }}</p>
      </div>
      <p v-if="refresh && refreshAfterId === null" class="rqa_muted rqa_small rqa_refresh_hint" role="status">{{ refreshText }}</p>
    </section>

    <footer v-if="!foreignOwner" class="rqa_composer">
      <template v-if="isClosed">
        <a href="#" class="rqa_link" @click.prevent="reopen">פתחי מחדש כדי להמשיך</a>
      </template>
      <template v-else>
        <textarea
          ref="input"
          :value="draft"
          class="rqa_followup_input"
          rows="3"
          :maxlength="maxChars"
          placeholder="כתבי הודעת המשך…"
          aria-label="הודעת המשך"
          @input="onInput"
          @keydown.ctrl.enter.prevent="send"
          @keydown.meta.enter.prevent="send"></textarea>
        <div class="rqa_composer_footer">
          <span class="rqa_counter">{{ draft.length }} / {{ maxChars }}</span>
          <button type="button" class="button is-info rqa_send" :class="{ 'is-loading': sending }" :disabled="!canSend" @click="send">שלחי</button>
        </div>
        <p v-if="sendError" class="rqa_error" role="alert">{{ sendError }}</p>
      </template>
    </footer>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import RecruiterQaStatusPill from './RecruiterQaStatusPill'
import RecruiterQaEntityText from './RecruiterQaEntityText'
import RecruiterQaEntityChips from './RecruiterQaEntityChips'
import RecruiterQaAttachment from './RecruiterQaAttachment'
import { rowSegments, unlinkedEntities } from '@/utils/recruiterQaEntities'
import { CHIP_LABELS } from '@/utils/recruiterQaContext'
import {
  ticketTitle, jiraShortId, jiraStatusQuestion, isCancelledBySupervisor, canGiveFeedback, canRetryTurn,
  isGenuineQuestion, isReAnswer, isSystemNote, reAnswerOf, recruiterTurnStatus, recruiterTicketStatus, RECRUITER_CANCELLED_TEXT, RE_ANSWER_LABEL, ANSWER_REFRESH_TEXT
} from '@/utils/recruiterQaFormat'
import { isPendingStatus, uuidv4 } from '@/store/modules/recruiterQa'

const MAX_CHARS = 4000

export default {
  name: 'RecruiterQaThread',
  components: { RecruiterQaStatusPill, RecruiterQaEntityText, RecruiterQaEntityChips, RecruiterQaAttachment },
  props: {
    rootId: { type: Number, required: true }
  },
  data () {
    return {
      sending: false,
      sendError: '',
      requestUuid: null,
      lifecycleBusy: false,
      confirmingClose: false,
      lifecycleError: '',
      turnErrors: {},
      retrying: null,
      reasonFor: null,
      reasonDraft: '',
      expanded: {},
      maxChars: MAX_CHARS,
      cancelledText: RECRUITER_CANCELLED_TEXT,
      reAnswerLabel: RE_ANSWER_LABEL,
      refreshText: ANSWER_REFRESH_TEXT
    }
  },
  computed: {
    ...mapState('recruiterQa', ['feedback', 'drafts', 'view', 'drawerOpen', 'adminId']),
    ...mapGetters('recruiterQa', { ticketOf: 'ticket', threadOf: 'thread', turnsOf: 'threadTurns', refreshOf: 'answerRefresh' }),
    ticket () { return this.ticketOf(this.rootId) },
    thread () { return this.threadOf(this.rootId) },
    turns () { return this.turnsOf(this.rootId) },
    title () {
      const fromTicket = this.ticket && this.ticket.title
      const firstQuestion = this.turns.filter(isGenuineQuestion)[0]
      const fromRoot = firstQuestion ? firstQuestion.question : ''
      return ticketTitle(fromTicket || fromRoot) || 'פנייה'
    },
    originalOwnerName () {
      const o = this.ticket && this.ticket.original_owner
      return (o && typeof o === 'object' && o.name) ? String(o.name) : ''
    },
    // Defensive: the server should never serve another owner's ticket; if it does, read-only.
    foreignOwner () {
      const o = this.ticket && this.ticket.owner
      if (o === undefined || o === null || this.adminId === null || this.adminId === undefined) return false
      const id = (typeof o === 'object') ? o.id : o
      return id !== undefined && id !== null && String(id) !== String(this.adminId)
    },
    isClosed () { return !!(this.ticket && this.ticket.closed_at) },
    // Pending updated answer: the previous answer stays; a neutral hint under it.
    refresh () { return this.refreshOf(this.rootId) },
    // question id -> true when a published re-answer is rendered under it
    reAnswered () {
      const map = {}
      this.turns.forEach(t => { const of = reAnswerOf(t); if (of) map[of] = true })
      return map
    },
    headerStatus () {
      const status = this.ticket && this.ticket.latest_status
      if (status !== 'superseded') return status || ''
      const latest = this.turns.filter(isGenuineQuestion).filter(t => t.status === 'superseded').pop()
      return latest ? this.statusOf(latest) : recruiterTicketStatus(status, !!this.refresh)
    },
    // Last rendered turn of the refreshed question (its newest answer), or null (end of list).
    refreshAfterId () {
      const r = this.refresh
      if (!r) return -1
      let after = null
      this.turns.forEach(t => { if (t.id === r.question_id || reAnswerOf(t) === r.question_id) after = t.id })
      return after
    },
    jiraIds () { return (this.ticket && this.ticket.jira_task_ids) || [] },
    draft () { return this.drafts[this.rootId] || '' },
    canSend () {
      const len = this.draft.trim().length
      return !this.sending && !this.isClosed && len > 0 && this.draft.length <= MAX_CHARS
    },
    renderedVersion () { return this.thread ? this.thread.notificationVersion : 0 }
  },
  watch: {
    // Ack only after the new content is actually in the DOM.
    renderedVersion () { this.$nextTick(this.ack) },
    rootId () { this.confirmingClose = false },
    turns (next, prev) {
      if ((next || []).length > (prev || []).length) this.$nextTick(this.scrollToBottom)
    }
  },
  mounted () {
    window.addEventListener('focus', this.ack)
    document.addEventListener('visibilitychange', this.ack)
    this.$nextTick(() => {
      this.scrollToBottom()
      this.ack()
      if (this.$refs.back) this.$refs.back.focus()
    })
  },
  beforeDestroy () {
    window.removeEventListener('focus', this.ack)
    document.removeEventListener('visibilitychange', this.ack)
  },
  methods: {
    isPendingStatus,
    reAnswer (turn) { return isReAnswer(turn) },
    systemNote (turn) { return isSystemNote(turn) },
    cancelled (turn) { return isCancelledBySupervisor(turn) },
    feedbackAllowed (turn) { return canGiveFeedback(turn) },
    statusOf (turn) {
      return recruiterTurnStatus(turn, {
        refreshPending: !!this.refresh && this.refresh.question_id === turn.id,
        hasPublishedReAnswer: !!this.reAnswered[turn.id]
      })
    },
    // A superseded question with nothing pending and nothing published is a plain failure.
    retryAllowed (turn) { return canRetryTurn({ ...turn, status: this.statusOf(turn) }) },
    jiraShort (id) { return jiraShortId(id) },
    ack () {
      // The store re-checks drawer/view/selection/focus/visibility.
      this.$store.dispatch('recruiterQa/ackRead', { rootId: this.rootId })
    },
    back () { this.$store.dispatch('recruiterQa/backToList') },
    reload () { this.$store.dispatch('recruiterQa/loadThread', { rootId: this.rootId }) },
    scrollToBottom () {
      const el = this.$refs.turns
      if (el) el.scrollTop = el.scrollHeight
    },
    // Any status may carry text; fall back to answer_long.
    answerText (turn) {
      const short = (turn.answer || '').trim()
      return short || (turn.answer_long || '').trim()
    },
    segmentsOf (turn, field) { return rowSegments(turn, field) },
    // Main bubble: the short answer, or answer_long when the answer is empty (cf. answerText).
    answerSegments (turn) {
      return (turn.answer || '').trim() ? rowSegments(turn, 'answer') : rowSegments(turn, 'answer_long')
    },
    // Entities the answer never names inline -> short related list under the answer.
    relatedOf (turn) { return unlinkedEntities(turn) },
    contextLine (turn) {
      const c = turn.context || {}
      const parts = []
      if (c.applicant_id) parts.push(CHIP_LABELS.applicant_id + ': ' + (c.applicant_name || '#' + c.applicant_id))
      if (c.position_id) parts.push(CHIP_LABELS.position_id + ': ' + (c.position_name || '#' + c.position_id))
      return parts.join(' · ')
    },
    sourceText (src) {
      if (!src) return ''
      return src.label || ((src.type || '') + (src.id ? ' #' + src.id : ''))
    },
    toggle (kind, id) {
      const key = kind + '_' + id
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] }
    },
    isOpen (kind, id) { return !!this.expanded[kind + '_' + id] },
    onInput (event) {
      this.$store.commit('recruiterQa/SET_DRAFT', { key: this.rootId, text: event.target.value })
      this.requestUuid = null // changed text = new idempotency key
      this.sendError = ''
    },
    async submitText (text, requestUuid) {
      this.sending = true
      this.sendError = ''
      const res = await this.$store.dispatch('recruiterQa/submit', {
        question: text,
        parentId: this.rootId,
        context: {},
        requestUuid
      })
      this.sending = false
      if (!res.ok) this.sendError = res.message
      return res
    },
    async send () {
      if (!this.canSend) return
      // Same key on a resend of the same text keeps it idempotent.
      if (!this.requestUuid) this.requestUuid = uuidv4()
      const res = await this.submitText(this.draft.trim(), this.requestUuid)
      if (res.ok) {
        this.requestUuid = null
        this.$store.commit('recruiterQa/SET_DRAFT', { key: this.rootId, text: '' })
      }
    },
    // "בדקי סטטוס": a normal follow-up; the typed draft stays untouched.
    checkJira (id) {
      if (this.isClosed || this.sending) return
      return this.submitText(jiraStatusQuestion(id), uuidv4())
    },
    async runLifecycle (action) {
      if (this.lifecycleBusy) return
      this.lifecycleBusy = true
      this.lifecycleError = ''
      const res = await this.$store.dispatch('recruiterQa/' + action, { rootId: this.rootId })
      this.lifecycleBusy = false
      if (!res.ok) this.lifecycleError = res.message
      return res
    },
    // Closing asks once inline; the reopen hint lives in that confirmation only.
    toggleLifecycle () {
      if (this.isClosed) return this.runLifecycle('reopenTicket')
      this.lifecycleError = ''
      this.confirmingClose = true
      this.$nextTick(() => { if (this.$refs.confirmClose) this.$refs.confirmClose.focus() })
    },
    async confirmClose () {
      const res = await this.runLifecycle('closeTicket')
      if (res && res.ok) this.confirmingClose = false
      return res
    },
    cancelClose () { this.confirmingClose = false },
    async reopen () {
      const res = await this.runLifecycle('reopenTicket')
      if (res && res.ok) this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus() })
    },
    async retry (id) {
      this.retrying = id
      this.turnErrors = { ...this.turnErrors, [id]: '' }
      const res = await this.$store.dispatch('recruiterQa/retry', { id })
      this.retrying = null
      if (!res.ok) this.turnErrors = { ...this.turnErrors, [id]: res.message }
    },
    askReason (id) {
      this.reasonFor = id
      this.reasonDraft = ''
    },
    async submitFeedback (id, helpful) {
      const res = await this.$store.dispatch('recruiterQa/sendFeedback', { id, helpful, reason: helpful ? null : this.reasonDraft.trim() })
      if (res.ok) {
        this.reasonFor = null
        this.reasonDraft = ''
      } else {
        this.turnErrors = { ...this.turnErrors, [id]: res.message }
      }
    }
  }
}
</script>
<style scoped>
.rqa_thread_view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.rqa_thread_top {
  padding: 8px 16px 0;
}
.rqa_back {
  background: none;
  border: none;
  color: #3273dc;
  cursor: pointer;
  font-size: 14px;
  padding: 6px 0;
  min-height: 36px;
}
.rqa_thread_header {
  padding: 4px 16px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.rqa_thread_title_row {
  display: flex;
  align-items: baseline;
}
.rqa_thread_title {
  flex: 1;
  min-width: 0;
  margin: 0 0 0 8px;
  font-size: 16px;
  font-weight: bold;
  word-break: break-word;
}
.rqa_nowrap {
  white-space: nowrap;
}
.rqa_thread_meta, .rqa_jira {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 6px;
}
.rqa_thread_meta > *, .rqa_jira > * {
  margin-left: 8px;
}
.rqa_closed_label {
  font-size: 12px;
  font-weight: bold;
  color: #555;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 0 6px;
}
.rqa_lifecycle {
  margin-right: auto;
}
.rqa_hint {
  margin: 6px 0 0;
}
.rqa_close_confirm {
  margin: 6px 0 0;
}
.rqa_close_confirm_actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}
.rqa_turns {
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px 16px;
}
.rqa_turn {
  padding: 10px 0;
}
.rqa_turn + .rqa_turn {
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}
.rqa_turn_head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.rqa_ctx {
  margin-right: 6px;
}
.rqa_question {
  font-weight: bold;
  white-space: pre-line;
  word-break: break-word;
  margin: 6px 0;
}
.rqa_answer_text, .rqa_answer_long {
  white-space: pre-line;
  word-break: break-word;
  margin: 4px 0;
}
.rqa_answer_long {
  color: #555;
  font-size: 13px;
}
.rqa_sources {
  margin: 4px 0;
  padding-right: 16px;
  list-style: disc;
}
.rqa_reanswer_label {
  font-weight: bold;
  color: #555;
  margin: 0 0 4px;
}
.rqa_refresh_hint {
  margin: 6px 0 0;
  font-style: italic;
}
.rqa_cancelled {
  margin: 6px 0 0;
}
.rqa_feedback, .rqa_failed {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 6px;
}
.rqa_feedback .button, .rqa_failed .button {
  margin-left: 6px;
}
.rqa_reason {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #333;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  padding: 6px 8px !important;
  margin-left: 6px;
  direction: rtl;
}
.rqa_composer {
  position: sticky;
  bottom: 0;
  background-color: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 10px 16px 12px;
}
.rqa_followup_input {
  display: block;
  width: 100%;
  min-width: 0;
  min-height: 84px;
  max-height: 240px;
  box-sizing: border-box;
  font-size: 15px;
  line-height: 1.5;
  color: #333;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  padding: 10px 12px !important;
  direction: rtl;
  resize: vertical;
}
.rqa_composer_footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}
.rqa_counter {
  font-size: 11px;
  color: #888;
}
.rqa_link {
  color: #3273dc;
  font-size: 13px;
  cursor: pointer;
}
.rqa_link:hover {
  color: #3273dc;
  text-decoration: underline;
}
.rqa_muted {
  color: #888;
}
.rqa_small {
  font-size: 12px;
}
.rqa_center {
  text-align: center;
  margin-top: 20px;
}
.rqa_error {
  color: #c9302c;
  font-size: 12px;
  margin: 6px 0 0;
}
@media (max-width: 767px) {
  .rqa_back, .rqa_composer .button, .rqa_thread_header .button { min-height: 44px; }
  .rqa_followup_input { min-height: 72px; font-size: 16px; }
}
</style>
